import type { useAppDispatch } from '../grid/reduxStore/Store';
import type { ErrorCode } from '../../../services/socket/SocketEvents';
import { AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents';
import { updateSectionTitle, moveSectionAfter } from '../grid/reduxStore/editorSlice';
import { setSaveStatus, setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { DocumentSaveStatus } from '../shared/models/DocumentSaveStatus';
import type { UndoRedoCommandInterface } from './undoRedoCommandVersion';
import { updateSectionViaSocket } from '../grid/reduxStore/saveHandlers';

export interface UpdateSectionCommandDependencies {
  dispatch: ReturnType<typeof useAppDispatch>;
  socketClient: any;
  checkDocumentLockStatus: (errorCode: ErrorCode) => boolean;
}

export interface SectionChangePayload {
  id: string;
  title?: string;
  moveAfterSectionId?: string | null;
}

export interface UpdateSectionCommandParams {
  sectionId: string;
  previousState: SectionChangePayload;
  newState: SectionChangePayload;
}

export class UpdateSectionCommand implements UndoRedoCommandInterface {
  readonly scope = 'section' as const;
  readonly timestamp: string;
  readonly sectionId: string;

  private readonly dispatch: ReturnType<typeof useAppDispatch>;
  private readonly socketClient: any;
  private readonly checkDocumentLockStatus: (errorCode: ErrorCode) => boolean;

  private readonly previousState: SectionChangePayload;
  private readonly newState: SectionChangePayload;

  constructor(dependencies: UpdateSectionCommandDependencies, params: UpdateSectionCommandParams) {
    this.timestamp = new Date().toISOString();
    this.dispatch = dependencies.dispatch;
    this.socketClient = dependencies.socketClient;
    this.checkDocumentLockStatus = dependencies.checkDocumentLockStatus;
    this.sectionId = params.sectionId;
    this.previousState = { ...params.previousState };
    this.newState = { ...params.newState };
  }

  async execute(): Promise<boolean> {
    return this.applyAndPersist(this.newState, this.previousState);
  }

  async undo(): Promise<boolean> {
    return this.applyAndPersist(this.previousState, this.newState);
  }

  async redo(): Promise<boolean> {
    return this.applyAndPersist(this.newState, this.previousState);
  }

  async persistToSocket(): Promise<boolean> {
    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const response = await updateSectionViaSocket(this.socketClient, this.buildSocketPayload(this.newState));

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.applyToRedux(this.previousState);
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.applyToRedux(this.previousState);
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          errorCode: response.errorCode,
        })
      );

      return true;
    } catch (error) {
      console.error('UpdateSectionCommand: persistToSocket failed', error);
      this.applyToRedux(this.previousState);
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return false;
    }
  }

  private async applyAndPersist(state: SectionChangePayload, rollbackState: SectionChangePayload): Promise<boolean> {
    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      this.applyToRedux(state);

      const response = await updateSectionViaSocket(this.socketClient, this.buildSocketPayload(state));

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.applyToRedux(rollbackState);
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.applyToRedux(rollbackState);
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          errorCode: response.errorCode,
        })
      );

      return true;
    } catch (error) {
      console.error('UpdateSectionCommand: failed', error);
      this.applyToRedux(rollbackState);
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return false;
    }
  }

  private applyToRedux(state: SectionChangePayload): void {
    if (state.title !== undefined) {
      this.dispatch(
        updateSectionTitle({
          sectionId: this.sectionId,
          sectionTitle: state.title,
        })
      );
    }

    if (state.moveAfterSectionId !== undefined) {
      this.dispatch(
        moveSectionAfter({
          sectionId: this.sectionId,
          afterSectionId: state.moveAfterSectionId,
        })
      );
    }
  }

  private buildSocketPayload(state: SectionChangePayload) {
    return {
      id: state.id,
      ...(state.title !== undefined && { title: state.title }),
      ...(state.moveAfterSectionId != null && { moveAfterSectionId: state.moveAfterSectionId }),
    };
  }

  toJSON(): Record<string, any> {
    return {
      sectionId: this.sectionId,
      previousState: this.previousState,
      newState: this.newState,
    };
  }
}
